import {
  action,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { ConditionalBanner } from '../types';

class BannerStore {
  @observable
  private banners = new Map<string, ConditionalBanner>();

  @observable
  private conditionalBanners = new Map<string, ConditionalBanner>();

  @observable
  private containerHeight: number | null = null;

  private disposers = new Map<string, () => void>();

  addConditionalBanner(
    conditionalBanner: ConditionalBanner,
  ): void {
    const { id, observableGetter } = conditionalBanner;
    this.removeConditionalBanner(id);

    const disposer = reaction(
      () => observableGetter(),
      (value) => {
        runInAction(() => {
          if (value) {
            this.banners.set(id, conditionalBanner);
          } else {
            this.banners.delete(id);
          }
        });
      },
      {
        fireImmediately: true,
        delay: 0,
      },
    );

    this.disposers.set(id, disposer);
    this.conditionalBanners.set(id, conditionalBanner);
  }

  @action
  removeConditionalBanner(id: string): void {
    const disposer = this.disposers.get(id);
    if (disposer) {
      disposer();
      this.disposers.delete(id);
    }
    this.conditionalBanners.delete(id);
    this.banners.delete(id);
  }

  @action
  clearAllConditionalBanners(): void {
    this.disposers.forEach(disposer => disposer());
    this.disposers.clear();
    this.conditionalBanners.clear();
    this.banners.clear();
  }

  getBanners(): ConditionalBanner[] {
    return Array.from(this.banners.values());
  }

  @action
  setContainerHeight(height: number): void {
    this.containerHeight = height;
  }

  getContainerHeight(): number | null {
    return this.containerHeight;
  }
}

export type { ConditionalBanner };
export default BannerStore;
